import { memo, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import type { AgendaEditValues, TeleconsultoriaAgendaSummary } from "../types";

const INPUT_DATE_FORMAT = "YYYY-MM-DD";

function initialValues(agenda: TeleconsultoriaAgendaSummary | null): AgendaEditValues {
  return {
    name: agenda?.name || "",
    periodStart: agenda?.periodStart ? dayjs(agenda.periodStart).format(INPUT_DATE_FORMAT) : "",
    periodEnd: agenda?.periodEnd ? dayjs(agenda.periodEnd).format(INPUT_DATE_FORMAT) : "",
  };
}

/**
 * Editar nome e período da agenda (mesmos campos do EditScheduleDialog do
 * ScheduleManage), sem sair da página de Teleconsultoria.
 */
export const EditAgendaDialog = memo(function EditAgendaDialog({
  agenda,
  isSaving,
  onClose,
  onSave,
}: {
  agenda: TeleconsultoriaAgendaSummary | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (agenda: TeleconsultoriaAgendaSummary, values: AgendaEditValues) => Promise<boolean>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [values, setValues] = useState<AgendaEditValues>(() => initialValues(agenda));

  useEffect(() => setValues(initialValues(agenda)), [agenda]);

  if (!agenda) return null;
  const isPeriodValid =
    Boolean(values.periodStart && values.periodEnd) &&
    !dayjs(values.periodEnd).isBefore(dayjs(values.periodStart), "day");
  const canSave = Boolean(values.name.trim()) && isPeriodValid && !isSaving;

  const handleSave = async () => {
    if (await onSave(agenda, values)) onClose();
  };

  return (
    <Dialog open onClose={() => !isSaving && onClose()} fullWidth maxWidth="xs" fullScreen={isMobile}>
      <DialogTitle>Editar agenda</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nome da agenda"
            value={values.name}
            onChange={(event) => setValues({ ...values, name: event.target.value })}
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Início"
              type="date"
              value={values.periodStart}
              onChange={(event) => setValues({ ...values, periodStart: event.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fim"
              type="date"
              value={values.periodEnd}
              onChange={(event) => setValues({ ...values, periodEnd: event.target.value })}
              InputLabelProps={{ shrink: true }}
              error={!isPeriodValid && Boolean(values.periodEnd)}
              helperText={!isPeriodValid && values.periodEnd ? "O fim deve ser depois do início." : ""}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={!canSave} sx={{ textTransform: "none" }}>
          {isSaving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
